// ═══════════════════════════════════════════════════════════════════════════════
// AgentBriefingPage — thin route wrapper around AgentBriefingTab
// ═══════════════════════════════════════════════════════════════════════════════
//
// Exposes the Patient Briefing tab as a standalone URL (`/agents/:id/briefings`)
// so admins can deep-link to it. The component itself is reusable — once the
// existing AgentDetailPage adds a Briefings tab, it can render the same
// <AgentBriefingTab> and this standalone page becomes the back-up surface
// (or be retired entirely without touching the component).
// ═══════════════════════════════════════════════════════════════════════════════

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Button, Space, Spin, Typography } from 'antd';
import { ArrowLeftOutlined, RobotOutlined } from '@ant-design/icons';
import { PageHeader } from '@/components/PageHeader';
import { HelpHotspot } from '@/features/help/HelpHotspot';
import { colors, typography } from '@/theme/tokens';
import { apiGet } from '@/api/client';
import type { OperationResult } from '@/api/types';
import { AgentBriefingTab } from './AgentBriefingTab';

const { Text } = Typography;

/**
 * Minimal agent shape needed for the page header. Mirrors the fields the
 * existing AgentDefinition type would carry — keeping the lookup focused
 * (one round-trip) rather than reusing the heavier agent registry store.
 */
interface AgentLite {
  id: number;
  displayName?: string;
  canonicalName?: string;
  audience?: string;
}

export function AgentBriefingPage() {
  const { id } = useParams<{ id: string }>();
  const agentId = Number(id);
  const navigate = useNavigate();
  const [agent, setAgent] = useState<AgentLite | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (!agentId) return;
    let cancelled = false;
    void (async () => {
      try {
        const res = await apiGet<OperationResult<AgentLite>>(`/api/Agent/GetById/${agentId}`);
        if (cancelled) return;
        if (!res.isSuccess || !res.data) {
          setLoadError(res.message ?? 'Agent not found');
          return;
        }
        setAgent(res.data);
      } catch (e: unknown) {
        if (!cancelled) setLoadError(e instanceof Error ? e.message : 'Failed to load agent');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [agentId]);

  if (loadError) {
    return (
      <Alert
        type="error"
        showIcon
        message="Agent not found"
        description={loadError}
        action={
          <Button onClick={() => navigate('/agents')} icon={<ArrowLeftOutlined />}>
            Back to agents
          </Button>
        }
      />
    );
  }

  if (!agent) {
    return (
      <div style={{ padding: 80, display: 'flex', justifyContent: 'center' }}>
        <Spin />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <PageHeader
        accent="violet"
        icon={
          <ArrowLeftOutlined
            onClick={() => navigate(`/agents/${agent.id}`)}
            style={{ cursor: 'pointer' }}
          />
        }
        title={
          <Space size={10} align="center">
            <RobotOutlined style={{ color: colors.violet[600] }} />
            <span>{agent.displayName ?? `Agent ${agent.id}`}</span>
            {agent.canonicalName && (
              <Text
                style={{ fontFamily: typography.fontMono, fontSize: 12.5, color: colors.slate[500] }}
              >
                {agent.canonicalName}
              </Text>
            )}
            <HelpHotspot topic="briefing-context.agent-link" />
          </Space>
        }
        subtitle="Patient briefing — composed context the agent receives every turn."
      />

      <AgentBriefingTab agentId={agent.id} />
    </div>
  );
}
